// Tip.js — the styled hover tooltip for every Sos2 surface.
//
// ⚠️ NEVER USE THE BARE `title` ATTRIBUTE (house rule). The native tooltip waits ~1s before it
// appears, renders tiny and unstyled in the OS chrome, cannot be positioned, cannot hold structure,
// and on a designed page it simply reads as a bug. This replaces it everywhere.
//
// Styling matches the Layered Read hover (`lrSnapshotApp.js`'s `.lrs-hover`, which CODEBASE_MAP
// names as the source of truth for hovers), so a hover anywhere in the product looks like that one.
//
// USAGE — delete the `title` and spread this in its place:
//     <button {...tip('Show me')} onClick={…}>          // was: title="Show me"
//     <span {...tip(cond ? 'a' : null)}>                // falsy text = no tooltip, no handlers
//
// WHY A PROPS SPREAD rather than a wrapper component: it is a ONE-LINE change per call site with no
// JSX restructuring, no closing tag to get wrong, and — the real reason — NO EXTRA ELEMENT IN THE
// FLOW. Most call sites here sit inside flex rows with measured widths, where an added wrapper
// silently changes layout. A wrapper would also need cloneElement + ref forwarding to measure its
// target, which breaks on any child that does not forward refs.
//
// The bubble is driven IMPERATIVELY against one reused DOM node rather than through React state.
// That is deliberate: these fire on mousemove across dense grids of dozens of targets, and a state
// update per hover would re-render the host component every time.
//
// Behaviour, all three parts deliberate — the same three the Blade tooltip rules call out:
//   DELAY    passing OVER a target opens nothing; only settling on one does (70ms).
//   SINGLE   one shared node, so a dozen tips can never stack. Dragging along a strip of targets
//            can skip an individual `mouseleave` entirely, which is exactly how that happens.
//   MEASURE  max-width 320px, left-aligned. The classic failure is a narrow centred column of
//            wrapped words; a tooltip nobody can read is worse than none.

const SHOW_DELAY = 70;
const GAP = 9;

let node = null;        // the single reused bubble
let timer = null;
let currentTarget = null;

function ensureNode() {
  if (node && node.isConnected) return node;
  node = document.createElement('div');
  node.setAttribute('role', 'tooltip');
  // zIndex clears the wiki drawer (199999) and the table overlay; a tooltip that opens BEHIND the
  // panel that raised it is the most common way this kind of layer goes wrong.
  node.style.cssText = [
    'position:fixed', 'z-index:200100', 'max-width:320px', 'text-align:left', 'pointer-events:none',
    'background:#0f2430', 'color:#eef4f6', 'border-radius:9px', 'padding:8px 11px',
    'font:400 12.5px/1.45 system-ui,-apple-system,sans-serif',
    'box-shadow:0 14px 38px rgba(0,0,0,.36)', 'opacity:0',
  ].join(';');
  document.body.appendChild(node);
  return node;
}

function hide() {
  clearTimeout(timer);
  currentTarget = null;
  document.removeEventListener('mouseover', onDocMouseOver, true);
  document.removeEventListener('click', onDocClick, true);
  if (node) { node.style.opacity = '0'; node.style.left = '-9999px'; }
}

// ⚠️ SAFETY NET for the tip outliving its target. `mouseleave` is not guaranteed: the element can
// UNMOUNT while the tip is open (React re-render, a panel closing, an error boundary swapping the
// tree) and then no leave event ever fires — the bubble just sits there over an empty page, which
// is exactly what a crash during testing showed. Also covers fast pointer traversal that skips the
// per-element leave. Cheap: bound only while a tip is actually open.
// ⚠️ WHY CLICKS ARE HANDLED HERE AND NOT AS AN onClick PROP (fixed 2026-08-27).
// `tip()` used to return `onClick: hide`. Every call site of the form
//     <button onClick={doTheThing} {...tip('label')}>
// therefore lost `doTheThing` entirely — the spread comes last and wins. It failed SILENTLY: the
// button still hovered, still showed its tooltip, still looked enabled, and did nothing. TEN
// controls were dead this way (the Layered Read "Text" tab, the tour button, the "Show me" cues,
// the history arrows, both breadcrumb backs, beta feedback, focus-act) and it was found only
// because a reader reported one of them. A document-level listener does the same job and cannot
// collide with anything a caller owns.
function onDocClick() { if (currentTarget) hide(); }

function onDocMouseOver(e) {
  if (!currentTarget) return;
  if (!currentTarget.isConnected || !(currentTarget === e.target || currentTarget.contains(e.target))) hide();
}

function showFor(el, text) {
  if (!el || !el.isConnected) return;
  const n = ensureNode();
  document.addEventListener('mouseover', onDocMouseOver, true);
  document.addEventListener('click', onDocClick, true);
  n.textContent = text;
  n.style.opacity = '1';
  const r = el.getBoundingClientRect();
  const b = n.getBoundingClientRect();
  // Flip below when there is no room above, and clamp horizontally so a tip on a far-left or
  // far-right control is never half off-screen.
  const above = r.top > b.height + GAP + 8;
  const half = b.width / 2;
  const cx = Math.min(Math.max(r.left + r.width / 2, half + 6), window.innerWidth - half - 6);
  n.style.left = `${Math.round(cx - half)}px`;
  n.style.top = `${Math.round(above ? r.top - GAP - b.height : r.bottom + GAP)}px`;
}

/**
 * Hover/focus props for a styled tooltip. Falsy text returns an empty object, so a conditional
 * tooltip needs no ternary at the call site: `{...tip(cond && 'text')}`.
 */
export function tip(text) {
  const t = (text === 0 ? '0' : text) || '';
  if (!t) return {};
  return {
    onMouseEnter: (e) => {
      const el = e.currentTarget;
      clearTimeout(timer);
      currentTarget = el;
      timer = setTimeout(() => { if (currentTarget === el) showFor(el, t); }, SHOW_DELAY);
    },
    onMouseLeave: hide,
    // Keyboard parity: a tooltip only reachable by mouse is not an accessible label.
    onFocus: (e) => showFor(e.currentTarget, t),
    onBlur: hide,
    // ⚠️ NO onClick HERE — EVER. See the note above showFor(): a props-spread helper that owns
    // `onClick` silently DELETES the consumer's own onClick at every call site that spreads after
    // it, and JSX makes that invisible. Clicks are handled by the document listener instead.
  };
}

export default tip;
